import type { FC } from 'react'

import { SelectableCard } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import GlobeIcon from 'mdi-react/GlobeIcon'
import PinIcon from 'mdi-react/PinIcon'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type {
  Customer as CustomerType,
  CustomerHead as CustomerHeadType,
} from '@/modules/ecom-policy/types'

import { useNewPolicyContext } from '../index.context'

const StepCustomer = () => {
  const { data: customerData } = useQuery(
    queries.customerData(),
    ecomPolicyResources.getCustomerData
  )

  if (!customerData || customerData.customers.length === 0) {
    return <NoResults>No available customers</NoResults>
  }

  return (
    <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {customerData?.customerHeads.map(customerHead => (
        <CustomerHead key={customerHead.id} {...customerHead} />
      ))}

      {customerData.customers.map(customer => (
        <Customer key={customer.id} {...customer} />
      ))}
    </div>
  )
}

const CustomerHead: FC<CustomerHeadType> = ({ id, name }) => {
  const newPolicy = useNewPolicyContext()
  const [selectedCustomerHeadId, setSelectedCustomerHeadId] = newPolicy.selectedCustomerHeadId
  const [, setSelectedCustomerId] = newPolicy.selectedCustomerId
  const [, setSelectedVendorInfo] = newPolicy.selectedVendorInfo

  const isSelected = selectedCustomerHeadId === id

  return (
    <div className="m-2">
      <SelectableCard
        bordered
        isSelected={isSelected}
        colorMode="blue"
        textLeft
        className="flex flex-col"
        padding={16}
        onClick={() => {
          setSelectedCustomerId(null)
          setSelectedVendorInfo({ ecomCustomerId: null, vendorId: null })
          setSelectedCustomerHeadId(id)
        }}
      >
        <h4 className="mb-0 mt-0.5 text-lg font-normal leading-none">
          <Translation id="Global" /> <GlobeIcon className="w-5" />
        </h4>

        <h3 className="mb-4 mt-1.5 text-2xl leading-none">{name}</h3>

        <div className="checkmark absolute right-2 top-2">
          {isSelected ? (
            <CheckboxMarkedCircleOutlineIcon className="h-8 w-8" />
          ) : (
            <CheckboxBlankCircleOutlineIcon className="h-8 w-8" />
          )}
        </div>
      </SelectableCard>
    </div>
  )
}

const Customer: FC<Pick<CustomerType, 'id' | 'name' | 'orgNo'>> = ({ id, name, orgNo }) => {
  const newPolicy = useNewPolicyContext()
  const [selectedCustomerId, setSelectedCustomerId] = newPolicy.selectedCustomerId
  const [, setSelectedCustomerHeadId] = newPolicy.selectedCustomerHeadId
  const [, setSelectedVendorInfo] = newPolicy.selectedVendorInfo

  const isSelected = selectedCustomerId === id

  return (
    <div className="m-2">
      <SelectableCard
        bordered
        textLeft
        padding={16}
        colorMode="yellow"
        isSelected={isSelected}
        onClick={() => {
          setSelectedCustomerHeadId(null)
          setSelectedVendorInfo({ ecomCustomerId: null, vendorId: null })
          setSelectedCustomerId(id)
        }}
      >
        <h4 className="mb-0 mt-0.5 text-lg font-normal leading-none">
          <Translation id="Local" /> <PinIcon className="w-5" />
        </h4>

        <h3 className="mb-4 mt-1.5 text-2xl leading-none">{name}</h3>

        <p className="m-0 text-sm">
          <Translation id="Org. number" />{' '}
          <em className="mt-1 block text-xl not-italic leading-none">{orgNo}</em>
        </p>

        <div className="checkmark absolute right-2 top-2">
          {isSelected ? (
            <CheckboxMarkedCircleOutlineIcon className="h-8 w-8" />
          ) : (
            <CheckboxBlankCircleOutlineIcon className="h-8 w-8" />
          )}
        </div>
      </SelectableCard>
    </div>
  )
}

export default StepCustomer
